import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    ShieldAlert, Plus, Pencil, Trash2, FileDown, FileText, Loader2, RefreshCw,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEVERITY = { 1: 'Minor', 2: 'Significant', 3: 'Considerable', 4: 'Major', 5: 'Catastrophic' };
const LIKELIHOOD = { 1: 'Very Low', 2: 'Low', 3: 'Medium', 4: 'High', 5: 'Very High' };
const CATEGORIES = ['Patient Identity', 'Data Quality', 'Clinical Decision Support', 'Integration',
    'AI / Clinical', 'Information Security', 'Algorithm', 'Integration / Data', 'Availability', 'Clinical'];
const STATUSES = ['Open', 'In Progress', 'Mitigated', 'Monitoring', 'Closed'];

const riskColor = (cls) => ({
    Low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Medium: 'bg-amber-50 text-amber-700 border-amber-200',
    High: 'bg-orange-50 text-orange-700 border-orange-200',
    Unacceptable: 'bg-rose-50 text-rose-700 border-rose-200',
}[cls] || 'bg-slate-100 text-slate-600 border-slate-200');

const statusColor = (s) => ({
    Open: 'bg-rose-50 text-rose-700 border-rose-200',
    'In Progress': 'bg-amber-50 text-amber-700 border-amber-200',
    Mitigated: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Monitoring: 'bg-sky-50 text-sky-700 border-sky-200',
    Closed: 'bg-slate-100 text-slate-500 border-slate-200',
}[s] || 'bg-slate-100 text-slate-600 border-slate-200');

const linesToArr = (s) => (s || '').split('\n').map((x) => x.trim()).filter(Boolean);
const arrToLines = (a) => (a || []).join('\n');

const emptyForm = {
    hazard_id: '', title: '', category: 'Clinical', description: '',
    causes: '', effects: '', controls: '', monitoring: '',
    initial_severity: '3', initial_likelihood: '3',
    residual_severity: '2', residual_likelihood: '1',
    owner: 'Mr Olufunso Onayemi (CSO)', status: 'Open',
};

function ScorePicker({ label, value, onChange, map, testid }) {
    return (
        <div className="space-y-1">
            <Label className="text-xs">{label}</Label>
            <Select value={String(value)} onValueChange={onChange}>
                <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
                <SelectContent>
                    {[1, 2, 3, 4, 5].map((n) => (
                        <SelectItem key={n} value={String(n)}>{n} — {map[n]}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

export default function HazardLogTab() {
    const [hazards, setHazards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState({ open: false, mode: 'create' });
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/admin/hazard-log`);
            setHazards(res.data || []);
        } catch (e) {
            toast.error('Failed to load hazard log');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setForm(emptyForm); setDialog({ open: true, mode: 'create' }); };
    const openEdit = (h) => {
        setForm({
            hazard_id: h.hazard_id, title: h.title, category: h.category, description: h.description,
            causes: arrToLines(h.causes), effects: arrToLines(h.effects), controls: arrToLines(h.controls),
            monitoring: h.monitoring || '',
            initial_severity: String(h.initial_severity), initial_likelihood: String(h.initial_likelihood),
            residual_severity: String(h.residual_severity), residual_likelihood: String(h.residual_likelihood),
            owner: h.owner || '', status: h.status || 'Open',
        });
        setDialog({ open: true, mode: 'edit' });
    };

    const save = async () => {
        if (!form.title.trim()) { toast.error('Title is required'); return; }
        setSaving(true);
        const payload = {
            title: form.title, category: form.category, description: form.description,
            causes: linesToArr(form.causes), effects: linesToArr(form.effects), controls: linesToArr(form.controls),
            monitoring: form.monitoring,
            initial_severity: Number(form.initial_severity), initial_likelihood: Number(form.initial_likelihood),
            residual_severity: Number(form.residual_severity), residual_likelihood: Number(form.residual_likelihood),
            owner: form.owner, status: form.status,
        };
        try {
            if (dialog.mode === 'create') {
                await axios.post(`${API}/admin/hazard-log`, payload);
                toast.success('Hazard added');
            } else {
                await axios.put(`${API}/admin/hazard-log/${form.hazard_id}`, payload);
                toast.success('Hazard updated');
            }
            setDialog({ open: false, mode: 'create' });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`${API}/admin/hazard-log/${deleteId}`);
            toast.success('Hazard removed');
            setDeleteId(null);
            load();
        } catch (e) {
            toast.error('Delete failed');
        }
    };

    const exportFile = async (kind) => {
        try {
            const res = await axios.get(`${API}/admin/hazard-log/export.${kind}`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `FendanaCor_Hazard_Log.${kind}`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(`Export failed`);
        }
    };

    return (
        <div className="space-y-4" data-testid="hazard-log-tab">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-rose-600 text-white grid place-items-center">
                        <ShieldAlert className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="text-base font-semibold text-slate-900">Clinical Hazard Log</h3>
                        <p className="text-xs text-slate-500">Live DCB0129 / DCB0160 clinical risk register · {hazards.length} hazards</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={load} data-testid="hazard-refresh-btn">
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => exportFile('csv')} data-testid="hazard-export-csv-btn">
                        <FileDown className="w-4 h-4 mr-1.5" /> CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => exportFile('pdf')} data-testid="hazard-export-pdf-btn">
                        <FileText className="w-4 h-4 mr-1.5" /> PDF
                    </Button>
                    <Button size="sm" className="btn-primary" onClick={openCreate} data-testid="hazard-add-btn">
                        <Plus className="w-4 h-4 mr-1.5" /> Add hazard
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="p-12 text-center text-slate-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" /> Loading hazard log...
                </div>
            ) : (
                <>
                    {/* Desktop table */}
                    <div className="hidden lg:block overflow-x-auto rounded-lg border border-slate-200">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600">
                                <tr className="text-left">
                                    <th className="p-3 font-semibold">ID</th>
                                    <th className="p-3 font-semibold">Hazard</th>
                                    <th className="p-3 font-semibold">Initial</th>
                                    <th className="p-3 font-semibold">Controls / mitigations</th>
                                    <th className="p-3 font-semibold">Residual</th>
                                    <th className="p-3 font-semibold">Status</th>
                                    <th className="p-3 font-semibold text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {hazards.map((h) => (
                                    <tr key={h.hazard_id} className="hover:bg-slate-50/60 align-top" data-testid={`hazard-row-${h.hazard_id}`}>
                                        <td className="p-3 font-mono font-semibold text-slate-700">{h.hazard_id}</td>
                                        <td className="p-3 max-w-xs">
                                            <p className="font-medium text-slate-800">{h.title}</p>
                                            <p className="text-xs text-slate-500">{h.category}</p>
                                        </td>
                                        <td className="p-3">
                                            <Badge variant="outline" className={riskColor(h.initial_risk_class)}>
                                                {h.initial_severity}×{h.initial_likelihood}={h.initial_risk_score}
                                            </Badge>
                                        </td>
                                        <td className="p-3 max-w-sm">
                                            <ul className="text-xs text-slate-600 list-disc pl-4 space-y-0.5">
                                                {(h.controls || []).slice(0, 4).map((c, i) => <li key={i}>{c}</li>)}
                                            </ul>
                                        </td>
                                        <td className="p-3">
                                            <Badge variant="outline" className={riskColor(h.residual_risk_class)}>
                                                {h.residual_severity}×{h.residual_likelihood}={h.residual_risk_score}
                                            </Badge>
                                            <p className="text-[10px] text-slate-400 mt-1">{h.risk_acceptability}</p>
                                        </td>
                                        <td className="p-3">
                                            <Badge variant="outline" className={statusColor(h.status)}>{h.status}</Badge>
                                        </td>
                                        <td className="p-3 text-right whitespace-nowrap">
                                            <Button variant="ghost" size="sm" onClick={() => openEdit(h)} data-testid={`hazard-edit-${h.hazard_id}`}>
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => setDeleteId(h.hazard_id)} data-testid={`hazard-delete-${h.hazard_id}`}>
                                                <Trash2 className="w-4 h-4 text-rose-500" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="lg:hidden space-y-3">
                        {hazards.map((h) => (
                            <Card key={h.hazard_id} className="card-clinical" data-testid={`hazard-card-${h.hazard_id}`}>
                                <CardContent className="p-4 space-y-2">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <p className="font-mono text-xs text-slate-500">{h.hazard_id} · {h.category}</p>
                                            <p className="font-semibold text-slate-800">{h.title}</p>
                                        </div>
                                        <Badge variant="outline" className={statusColor(h.status)}>{h.status}</Badge>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap text-xs">
                                        <span className="text-slate-500">Initial</span>
                                        <Badge variant="outline" className={riskColor(h.initial_risk_class)}>{h.initial_risk_score} {h.initial_risk_class}</Badge>
                                        <span className="text-slate-500">→ Residual</span>
                                        <Badge variant="outline" className={riskColor(h.residual_risk_class)}>{h.residual_risk_score} {h.residual_risk_class}</Badge>
                                    </div>
                                    <p className="text-xs text-slate-600">{h.description}</p>
                                    <div className="flex justify-end gap-1 pt-1">
                                        <Button variant="outline" size="sm" onClick={() => openEdit(h)}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                                        <Button variant="outline" size="sm" onClick={() => setDeleteId(h.hazard_id)}><Trash2 className="w-3.5 h-3.5 mr-1 text-rose-500" />Delete</Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </>
            )}

            {/* Add / edit dialog */}
            <Dialog open={dialog.open} onOpenChange={(o) => setDialog({ ...dialog, open: o })}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hazard-dialog">
                    <DialogHeader>
                        <DialogTitle>{dialog.mode === 'create' ? 'Add hazard' : `Edit ${form.hazard_id}`}</DialogTitle>
                        <DialogDescription>Severity × Likelihood (1–5) determines the risk rating automatically.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1">
                            <Label className="text-xs">Title</Label>
                            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="hazard-title-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs">Category</Label>
                                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                    <SelectTrigger data-testid="hazard-category-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Status</Label>
                                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                    <SelectTrigger data-testid="hazard-status-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Description</Label>
                            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="hazard-description-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs">Causes (one per line)</Label>
                                <Textarea rows={3} value={form.causes} onChange={(e) => setForm({ ...form, causes: e.target.value })} />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Effects (one per line)</Label>
                                <Textarea rows={3} value={form.effects} onChange={(e) => setForm({ ...form, effects: e.target.value })} />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Controls / mitigations (one per line)</Label>
                            <Textarea rows={3} value={form.controls} onChange={(e) => setForm({ ...form, controls: e.target.value })} data-testid="hazard-controls-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-3 pt-1">
                            <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                                <p className="text-xs font-semibold text-slate-500">Initial risk</p>
                                <ScorePicker label="Severity" value={form.initial_severity} map={SEVERITY} onChange={(v) => setForm({ ...form, initial_severity: v })} testid="hazard-init-sev" />
                                <ScorePicker label="Likelihood" value={form.initial_likelihood} map={LIKELIHOOD} onChange={(v) => setForm({ ...form, initial_likelihood: v })} testid="hazard-init-lik" />
                            </div>
                            <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                                <p className="text-xs font-semibold text-slate-500">Residual risk</p>
                                <ScorePicker label="Severity" value={form.residual_severity} map={SEVERITY} onChange={(v) => setForm({ ...form, residual_severity: v })} testid="hazard-res-sev" />
                                <ScorePicker label="Likelihood" value={form.residual_likelihood} map={LIKELIHOOD} onChange={(v) => setForm({ ...form, residual_likelihood: v })} testid="hazard-res-lik" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs">Owner</Label>
                                <Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Monitoring</Label>
                                <Input value={form.monitoring} onChange={(e) => setForm({ ...form, monitoring: e.target.value })} />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialog({ ...dialog, open: false })}>Cancel</Button>
                        <Button className="btn-primary" onClick={save} disabled={saving} data-testid="hazard-save-btn">
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog open={!!deleteId} onOpenChange={(o) => { if (!o) setDeleteId(null); }}>
                <DialogContent data-testid="hazard-delete-dialog">
                    <DialogHeader>
                        <DialogTitle>Remove hazard {deleteId}?</DialogTitle>
                        <DialogDescription>This removes the hazard from the live log. This action cannot be undone.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
                        <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={confirmDelete} data-testid="hazard-delete-confirm">Remove</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
